import { useEffect, useRef, useState } from "react";
import { copyText } from "../../lib/browser";
import { SITE_URL } from "../../lib/seo";

interface Props {
  title: string;
  niche: string;
  slug: string;
  testId?: string;
}

const BASE = "flex items-center gap-2 rounded-lg border py-2 text-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500";

const svg = (path: string) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
    <path d={path} />
  </svg>
);

const ICONS = {
  whatsapp: svg("M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35ZM12.04 21.5h-.01a9.43 9.43 0 0 1-4.8-1.31l-.35-.2-3.57.93.95-3.48-.22-.36a9.4 9.4 0 0 1-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.98 6.67 2.77a9.37 9.37 0 0 1 2.76 6.68c0 5.2-4.24 9.43-9.44 9.43Zm8.04-17.47A11.3 11.3 0 0 0 12.04.7C5.77.7.67 5.8.67 12.06c0 2 .52 3.96 1.52 5.68L.57 23.7l6.1-1.6a11.33 11.33 0 0 0 5.37 1.37h.01c6.26 0 11.36-5.1 11.36-11.37 0-3.03-1.18-5.89-3.33-8.03Z"),
  link: svg("M10.59 13.41a1 1 0 0 1 0-1.41l3.3-3.3a1 1 0 1 1 1.41 1.42l-3.3 3.29a1 1 0 0 1-1.41 0Zm-2.12 6.36a4 4 0 0 1-2.83-6.83l2.12-2.12a1 1 0 0 1 1.42 1.41l-2.13 2.13a2 2 0 0 0 2.83 2.83l2.12-2.13a1 1 0 1 1 1.42 1.42l-2.13 2.12a3.98 3.98 0 0 1-2.82 1.17Zm7.07-7.07a1 1 0 0 1-.71-1.71l2.13-2.12a2 2 0 0 0-2.83-2.83l-2.12 2.13a1 1 0 0 1-1.42-1.42l2.13-2.12a4 4 0 0 1 5.65 5.65l-2.12 2.13a1 1 0 0 1-.71.29Z"),
  check: svg("M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17Z"),
  telegram: svg("M21.94 4.64 18.9 19a1.1 1.1 0 0 1-1.62.68l-4.6-3.4-2.22 2.14a.75.75 0 0 1-.6.29l.22-3.1 5.64-5.1c.25-.22-.05-.34-.38-.12l-6.97 4.39-3-.94c-.65-.2-.66-.65.14-.97l11.73-4.52c.54-.2 1.02.13.7 1.29Z"),
  x: svg("M17.75 3h3.07l-6.71 7.67L22 21h-6.18l-4.84-6.33L5.44 21H2.37l7.18-8.2L2 3h6.34l4.37 5.78L17.75 3Zm-1.08 16.2h1.7L7.4 4.73H5.58L16.67 19.2Z"),
};

export function SocialShareBar({ title, niche, slug, testId = "social-share" }: Props) {
  const url = `${SITE_URL}/${niche}/${slug}`;
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onCopy = async () => {
    if (!(await copyText(url))) return;
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const enc = encodeURIComponent;
  const ext = { target: "_blank", rel: "noopener noreferrer" } as const;
  return (
    <div data-testid={testId} className="my-8 print:hidden">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Compartilhe com quem precisa desta orientação</p>
      <div className="flex flex-wrap items-center gap-2.5">
        <a
          {...ext}
          href={`https://api.whatsapp.com/send?text=${enc(`Olha este guia sobre ${title}: ${url}`)}`}
          aria-label="Compartilhar no WhatsApp"
          data-testid={`${testId}-whatsapp`}
          className={`${BASE} border-[#25D366]/30 bg-[#25D366]/15 px-4 text-[#25D366] hover:bg-[#25D366]/25`}
        >
          {ICONS.whatsapp} WhatsApp
        </a>
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copiar link do guia"
          data-testid={`${testId}-copy`}
          data-copied={copied}
          className={`${BASE} px-4 ${copied ? "border-emerald-500/40 bg-slate-800/80 text-emerald-400" : "border-slate-700 bg-slate-800/80 text-slate-200 hover:bg-slate-700/80"}`}
        >
          {copied ? ICONS.check : ICONS.link} {copied ? "Link copiado!" : "Copiar Link"}
        </button>
        <a
          {...ext}
          href={`https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`}
          aria-label="Compartilhar no Telegram"
          data-testid={`${testId}-telegram`}
          className={`${BASE} border-sky-500/30 bg-sky-500/15 px-3 text-sky-400 hover:bg-sky-500/25`}
        >
          {ICONS.telegram} Telegram
        </a>
        <a
          {...ext}
          href={`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`}
          aria-label="Compartilhar no X"
          data-testid={`${testId}-x`}
          className={`${BASE} border-slate-700 bg-slate-800/60 px-3 text-slate-300 hover:bg-slate-800`}
        >
          {ICONS.x} X
        </a>
      </div>
    </div>
  );
}
